"use client";

/**
 * Renders CMS-driven Menu Header Code banners (image or HTML) either above the
 * header or below the breaking-news bar.
 *
 * The full set of active codes + the route→pageId map are embedded by the root
 * layout (server) into props, so first paint is correct (no CLS / flash). This
 * is a client component so it re-resolves on SPA navigation (`usePathname`) and
 * swaps the desktop/mobile variant when the viewport crosses the lg breakpoint —
 * the root layout never re-renders on soft navigation, so doing this on the
 * server alone would leave url_based / page_based banners stale.
 */
import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { getActiveMenuHeaderBanners, type ActiveMenuHeaderBanner } from "@/lib/menu-header-codes-helpers";
import type { MenuHeaderCodeConfig, MenuHeaderPosition } from "@/lib/local-store/types";
interface Props {
  codes: Record<string, MenuHeaderCodeConfig>;
  routePageIds: Record<string, string>;
  position: MenuHeaderPosition;
  /** Server-detected mobile (from UA) — used for the first render before hydration. */
  initialIsMobile: boolean;
}

/** Re-execute <script> tags inside an HTML slot (dangerouslySetInnerHTML never runs them). */
function HtmlSlot(t0) {
  const $ = _c(5);
  const {
    html
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const container = ref.current;
      if (!container) {
        return;
      }
      const scripts = Array.from(container.querySelectorAll("script"));
      for (const old of scripts) {
        const s = document.createElement("script");
        for (const attr of Array.from(old.attributes)) {
          s.setAttribute(attr.name, attr.value);
        }
        if (old.textContent) {
          s.textContent = old.textContent;
        }
        old.parentNode?.replaceChild(s, old);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== html) {
    t2 = [html];
    $[1] = html;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== html) {
    t3 = <div ref={ref} dangerouslySetInnerHTML={{
      __html: html
    }} />;
    $[3] = html;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function Banner(t0) {
  const $ = _c(9);
  const {
    banner
  } = t0;
  const {
    slot
  } = banner;
  if (slot.type === "image" && slot.imageUrl) {
    const t1 = slot.imageAlt || "";
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        width: "100%",
        height: "auto",
        display: "block"
      };
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== slot.imageUrl || $[2] !== t1) {
      t3 = <img src={slot.imageUrl} alt={t1} style={t2} loading="lazy" />;
      $[1] = slot.imageUrl;
      $[2] = t1;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const img = t3;
    let t4;
    if ($[4] !== img || $[5] !== slot.imageLink) {
      t4 = <div className="max-w-[1245px] mx-auto w-full">{slot.imageLink ? <a href={slot.imageLink} target="_blank" rel="noopener noreferrer">{img}</a> : img}</div>;
      $[4] = img;
      $[5] = slot.imageLink;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (slot.type === "html" && slot.html) {
    let t1;
    if ($[7] !== slot.html) {
      t1 = <div className="max-w-[1245px] mx-auto w-full"><HtmlSlot html={slot.html} /></div>;
      $[7] = slot.html;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    return t1;
  }
  return null;
}
export default function MenuHeaderCodes(t0) {
  const $ = _c(13);
  const {
    codes,
    routePageIds,
    position,
    initialIsMobile
  } = t0;
  const pathname = usePathname() || "/";
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const mq = window.matchMedia("(max-width: 1023px)");
      const update = () => setIsMobile(mq.matches);
      update();
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== codes || $[3] !== isMobile || $[4] !== pathname || $[5] !== position || $[6] !== routePageIds) {
    t3 = getActiveMenuHeaderBanners(codes, pathname, isMobile, routePageIds, position);
    $[2] = codes;
    $[3] = isMobile;
    $[4] = pathname;
    $[5] = position;
    $[6] = routePageIds;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const banners = t3;
  if (banners.length === 0) {
    return null;
  }
  let t4;
  if ($[8] !== banners) {
    t4 = banners.map(_temp);
    $[8] = banners;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== position || $[11] !== t4) {
    t5 = <div className="w-full" data-menu-header-position={position}>{t4}</div>;
    $[10] = position;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp(banner) {
  return <Banner key={banner.slug} banner={banner} />;
}